import * as React from "react";
import { useLocation, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Home } from "lucide-react";

interface HeaderProps {
  className?: string;
  title?: string;
  actions?: React.ReactNode;
}

const breadcrumbMap: Record<string, string> = {
  "/": "Listings",
  "/jobs": "Jobs",
  "/profiles": "Profiles",
  "/listings": "Listings",
  "/listings/:id": "Listing Details",
  "/settings": "Settings",
};

export function Header({ className, title, actions }: HeaderProps) {
  const location = useLocation();
  const pathname = location.pathname;

  // Generate breadcrumbs - exclude current page to avoid duplication with title
  const pathSegments = pathname.split("/").filter(Boolean);
  const parentBreadcrumbs = pathSegments.slice(0, -1).map((segment, index) => {
    const href = "/" + pathSegments.slice(0, index + 1).join("/");
    const label = breadcrumbMap[href] || segment.charAt(0).toUpperCase() + segment.slice(1);
    return { href, label };
  });

  const pageTitle = title || breadcrumbMap[pathname] || (pathSegments.length > 0 
    ? pathSegments[pathSegments.length - 1].charAt(0).toUpperCase() + pathSegments[pathSegments.length - 1].slice(1)
    : "Listings");

  // Don't show breadcrumbs on root workspace page
  const showBreadcrumbs = parentBreadcrumbs.length > 0;

  return (
    <header
      className={cn(
        "sticky top-0 z-30 w-full border-b border-[#e5e5e5] bg-[#ffffff]",
        className
      )}
    >
      <div className="flex h-16 min-w-0 items-center justify-between pl-16 pr-4 lg:px-6">
        <div className={cn("flex min-w-0 flex-1 flex-col", showBreadcrumbs ? "gap-0" : "justify-center h-full")}>
          {/* Breadcrumbs - only show parent paths */}
          {showBreadcrumbs && (
            <nav className="flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap text-xs text-[#888888]">
              <NavLink
                to="/"
                className="flex items-center gap-1 hover:text-[#555555] transition-colors"
              >
                <Home className="h-3 w-3" />
              </NavLink>
              <span className="text-[#cccccc]">/</span>
              {parentBreadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.href}>
                  {index > 0 && <span className="text-[#cccccc]">/</span>}
                  <NavLink
                    to={crumb.href}
                    className="hover:text-[#555555] transition-colors"
                  >
                    {crumb.label}
                  </NavLink>
                </React.Fragment>
              ))}
            </nav>
          )}
          {/* Page Title */}
          <h1 className={cn(
            "max-w-full truncate font-semibold text-[#000000]",
            showBreadcrumbs ? "text-base leading-tight" : "text-lg"
          )}>{pageTitle}</h1>
        </div>
        {/* Actions */}
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}

export default Header;
